import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  InputAdornment,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Description as DocumentIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { documentService } from '../../services/document.service';
import { formatFileSize } from '../../utils/dateUtils';

interface DocumentSelectorProps {
  label: string;
  selectedDocumentId?: string;
  selectedFileName?: string;
  contentPreview?: string;
  onSelect: (documentId: string, content: string, fileName: string, title: string) => void;
  onClear: () => void;
  error?: string;
  disabled?: boolean;
}

interface DocumentItem {
  id: string;
  title: string;
  fileName?: string;
  category?: string | { id: string; code: string; name: string; description?: string };
  size?: number;
  fileSize?: number;
  tags?: string[];
  summary?: string;
}

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  label,
  selectedDocumentId,
  selectedFileName,
  contentPreview,
  onSelect,
  onClear,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);

  // Fetch documents when dialog opens
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await documentService.getDocuments({
        pageSize: 50,
        sortBy: 'date',
        sortOrder: 'desc',
      });
      setDocuments(response.results || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setFetchError(t('drbfm.documentSelector.fetchError', 'ドキュメント一覧の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (dialogOpen) {
      fetchDocuments();
    }
  }, [dialogOpen, fetchDocuments]);

  // Helper to get category name from category (can be string or object)
  const getCategoryName = (category?: string | { id: string; code: string; name: string; description?: string }): string | undefined => {
    if (!category) return undefined;
    if (typeof category === 'string') return category;
    return category.name;
  };

  // Filter documents based on search query
  const filteredDocuments = documents.filter((doc) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const categoryName = getCategoryName(doc.category);
    return (
      doc.title?.toLowerCase().includes(query) ||
      doc.fileName?.toLowerCase().includes(query) ||
      categoryName?.toLowerCase().includes(query) ||
      doc.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  const handleOpenDialog = () => {
    if (!disabled) {
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSearchQuery('');
  };

  const handleDocumentSelect = async (doc: DocumentItem) => {
    setContentLoading(true);
    try {
      const contentResponse = await documentService.getDocumentContent(doc.id);
      onSelect(
        doc.id,
        contentResponse.content,
        contentResponse.file_name || doc.fileName || doc.title,
        contentResponse.title || doc.title
      );
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to fetch document content:', err);
      setFetchError(t('drbfm.documentSelector.contentError', 'ドキュメントの内容を取得できませんでした'));
    } finally {
      setContentLoading(false);
    }
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <Box>
      {/* Selected Document Display or Select Button */}
      {selectedDocumentId ? (
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <DocumentIcon color="primary" />
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedFileName || t('drbfm.documentSelector.documentSelected')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('drbfm.documentSelector.selected')}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  size="small"
                  onClick={handleOpenDialog}
                  disabled={disabled}
                  sx={{ mr: 1 }}
                >
                  {t('drbfm.documentSelector.change', '変更')}
                </Button>
                <IconButton
                  size="small"
                  onClick={handleClear}
                  disabled={disabled}
                  color="error"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Content Preview */}
            {contentPreview && (
              <Paper
                variant="outlined"
                sx={{
                  mt: 1,
                  p: 1,
                  maxHeight: 150,
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {contentPreview.slice(0, 500)}
                  {contentPreview.length > 500 && '...'}
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outlined"
          fullWidth
          onClick={handleOpenDialog}
          disabled={disabled}
          startIcon={<DocumentIcon />}
          sx={{
            justifyContent: 'flex-start',
            py: 1.5,
            borderStyle: error ? 'solid' : 'dashed',
            borderColor: error ? 'error.main' : 'divider',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {label}
        </Button>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}

      {/* Document Selection Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle>
          {t('drbfm.documentSelector.selectDocument', 'ドキュメントを選択')}
        </DialogTitle>
        <DialogContent dividers>
          {/* Search Field */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('drbfm.documentSelector.searchPlaceholder', 'ドキュメントを検索...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Error Display */}
          {fetchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fetchError}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredDocuments.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                {searchQuery
                  ? t('drbfm.documentSelector.noResults', '検索結果がありません')
                  : t('drbfm.documentSelector.noDocuments', 'ドキュメントがありません')
                }
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredDocuments.map((doc) => (
                <ListItem
                  key={doc.id}
                  disablePadding
                  sx={{ mb: 0.5 }}
                >
                  <ListItemButton
                    onClick={() => handleDocumentSelect(doc)}
                    disabled={contentLoading}
                    selected={doc.id === selectedDocumentId}
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: doc.id === selectedDocumentId ? 'primary.main' : 'divider',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">
                            {doc.title}
                          </Typography>
                          {doc.id === selectedDocumentId && (
                            <CheckIcon color="primary" fontSize="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {doc.fileName && doc.fileName !== doc.title && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {doc.fileName}
                            </Typography>
                          )}
                          <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                            {getCategoryName(doc.category) && (
                              <Chip
                                label={getCategoryName(doc.category)}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {(doc.size || doc.fileSize) && (
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(doc.size || doc.fileSize || 0)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* Content Loading Overlay */}
          {contentLoading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255,255,255,0.8)"
              zIndex={1}
            >
              <Box textAlign="center">
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {t('drbfm.documentSelector.loadingContent', 'コンテンツを読み込み中...')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('common.cancel', 'キャンセル')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentSelector;
